import {mergeStyles} from 'components/FluentUICompat';
import React from 'react';
import DOMPurify from 'dompurify';
import 'react-quill-new/dist/quill.bubble.css';
import 'components/quill/registerQuillModules';

type RichtextDisplayProps = {
	value: string;
	limit?: number;
	removeHtml?: boolean;
};

const SANITIZE_CONFIG: DOMPurify.Config = {
	ADD_TAGS: ['table', 'thead', 'tbody', 'tr', 'td', 'th', 'col', 'colgroup'],
	ADD_ATTR: ['class', 'style', 'colspan', 'rowspan', 'width', 'data-*'],
};

/**
 * Converts an HTML string to plain text. Block and cell boundaries are turned
 * into spaces first so that adjacent words are not concatenated.
 *
 * Text is extracted via the DOM rather than tag-stripping regexes to avoid the
 * super-linear backtracking (ReDoS) risk those patterns carry on hostile input.
 */
const toPlainText = (html: string): string => {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	doc.body.querySelectorAll('td, tr, p, br').forEach(element => {
		element.insertAdjacentText(
			element.tagName === 'BR' ? 'afterend' : 'beforeend',
			' ',
		);
	});
	return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
};

export const RichtextDisplay: React.FC<RichtextDisplayProps> = ({
	value,
	limit = Infinity,
	removeHtml = false,
}) => {
	const quillClassName = mergeStyles({
		'.ql-editor': {
			padding: 0,
		},
	});

	if (removeHtml || limit !== Infinity) {
		const plainText = toPlainText(value);
		const truncated = plainText
			.substring(0, limit)
			.concat(plainText.length > limit ? '...' : '');
		return <>{truncated}</>;
	}

	const sanitized = DOMPurify.sanitize(value, SANITIZE_CONFIG);

	return (
		<div className={`ql-bubble ${quillClassName}`}>
			<div
				className='ql-editor'
				dangerouslySetInnerHTML={{__html: sanitized}}
			/>
		</div>
	);
};
